<template>
<div class="bundle-details">
    <section
        class="hero grid" :style="heroStyles"
    >
        <div class="shopping-overlay padded clamped">
            <PackShoppingOverlay
                v-if="product && bundle"
                :pack="bundle"
                :product="product"
                :show-platforms="true"
                :fell-back-to-en="fellBackToEn"
                :is-bundle="true"
            />
        </div>
    </section>

    <div v-if="bundle?.promotional?.data" class="media-section clamped">
        <h3 v-t="'PACK_DETAIL.TRAILERS'" class="section-heading title-6" />
        <section class="media mobile">
            <Carousel
                :media="bundle?.promotional?.data"
                v-on:item-clicked="carouselClick"
            />
        </section>
    </div>

    <div v-if="gameDetailsPerPack" class="games-section clamped">
        <h3 class="section-heading title-6">
            {{ $t('PACK_DETAIL.FEATURED_TITLES', { numGamesInPack: gamesInBundle.length }) }}
        </h3>

        <section v-if="gamesInBundle" class="game-details">
            <NuxtLink v-for="game in gamesInBundle" :key="game.slug" :to="linkUrl(game)">
                <div class="game-card">
                    <div class="hero-overlay" />
                    <NuxtImg
                        v-if="game?.croppedHero?.data?.attributes?.url"
                        :src="`${game.croppedHero.data.attributes.url}?format=auto`"
                        class="cropped-hero" :alt="game?.croppedHero?.data?.attributes.alternativeText || ''"
                    />
                    <NuxtImg
                        v-if="game?.wordmark?.data?.attributes?.url"
                        :src="`${game.wordmark.data?.attributes.url}?format=auto`"
                        class="wordmark" :alt="game?.wordmark?.data?.attributes.alternativeText || ''"
                    />
                    <div class="tag-container">
                        <Tag
                            v-if="game.features.types"
                            :text="gameType(game)?.name"
                            :icon="gameType(game)?.icon"
                            feature="gameType"
                        />
                        <Tag
                            v-if="getPlayerRange(game)"
                            feature="playerRange"
                            :text="getPlayerRange(game)"
                            icon="fa fa-regular fa-user-group"
                        />
                        <Tag
                            v-if="getDuration(game)"
                            feature="duration"
                            :text="`${getDuration(game)}`"
                            icon="fa fa-regular fa-clock-eleven-thirty"
                        />
                    </div>
                    <p class="description">{{ game.shortDescription }}</p>
                </div>
            </NuxtLink>
        </section>
    </div>

    <div class="about-section clamped">
        <h1 v-t="'PACK_DETAIL.ABOUT'" class="section-heading title-6" />
        <section class="about">
            <div class="padded about-container" :class="{ 'flex-row': windowWidth > 959, 'flex-column': windowWidth < 960 }">
                <!--label-->
                <div class="description">
                    <p class="heading">{{ $t('PACK_DETAIL.DESCRIPTION') }}</p>
                    <p v-md="bundle?.description" />
                </div>
                <div v-if="specs" class="specs">
                    <p class="heading">{{ $t('PACK_DETAIL.SPECS') }}</p>
                    <div class="attributes">
                        <div class="details flex-column">
                            <div class="detail">
                                <p>{{ $t('GAME_DETAIL.PLAYER_COUNT_HEADER') }}</p>
                                <p v-if="specs.playerCounts" class="right">
                                    {{
                                        $t('PACK_DETAIL.PLAYER_COUNT_RANGE', {
                                            playerMin: Math.min(...specs.playerCounts),
                                            playerMax: Math.max(...specs.playerCounts)
                                        })
                                    }}
                                </p>
                            </div>
                            <div class="detail">
                                <p>{{ $t('GAME_DETAIL.DURATION_HEADER') }}</p>
                                <p v-if="specs.durations" class="right">
                                    {{
                                        $t('PACK_DETAIL.DURATION_RANGE', {
                                            durationMin: Math.min(...specs.durations),
                                            durationMax: Math.max(...specs.durations)
                                        })
                                    }}
                                </p>
                            </div>
                            <div class="detail">
                                <p>{{ $t('PACK_DETAIL.GAME_TYPES') }}</p>
                                <p v-if="specs.types" class="right">
                                    {{
                                        (specs.types.filter(
                                            (tag: string, idx: number) => specs?.types.indexOf(tag) === idx
                                        )).join(', ')
                                    }}
                                </p>
                            </div>
                            <div class="detail">
                                <p>{{ $t('GAME_DETAIL.FAMILY_FRIENDLY') }}</p>
                                <p v-if="specs.ffSetting && (specs.ffSetting.indexOf(true) > -1)">Optional</p>
                                <i v-else class="fa-solid fa-circle-xmark" />
                            </div>
                            <div class="detail">
                                <p>{{ $t('GAME_DETAIL.MODERATION') }}</p>
                                <div v-if="specs.modSetting">
                                    <i v-if="specs.modSetting.indexOf(true) > -1" class="fa-solid fa-circle-check" />
                                    <i v-else class="fa-solid fa-circle-xmark" />
                                </div>
                            </div>
                            <div class="detail">
                                <p>{{ $t('PACK_DETAIL.AUD_PLAY_ALONG') }}</p>
                                <template v-if="specs.audienceSetting">
                                    <p v-if="specs.audienceSetting.indexOf(true) > -1" class="right audience-count">
                                        {{ $t('PACK_DETAIL.AUD_CAPACITY') }}
                                    </p>
                                    <i v-else class="fa-solid fa-circle-xmark" />
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</div>
</template>

<script setup lang="ts">
import type { Component, CSSProperties } from 'vue'
import type { Shopify } from '@jackboxgames/shopify'
import type { JBGWeb } from '$types/JBGWeb'
import { I18n } from '$services/i18n'
import PackShoppingOverlay from '$components/shopping/PackShoppingOverlay.vue'
import Carousel from '$components/carousel/Carousel.vue'
import CarouselModal from '$components/carousel/CarouselModal.vue'
import Tag from '$components/Tag.vue'
import { currentCountry, ShoppingCart } from '$services/shopify'
import currencyCodeCloudfrontRegionMap from '$components/shopping/currencyCodeCloudfrontRegionMap.json'

const app = useNuxtApp()
const i18n = useI18n()
const route = useRoute()
const localeRoute = useLocaleRoute()
const windowWidth = ref(800)
const fellBackToEn = ref(false)
const bundleSlug = route.params.bundleslug as string
const shouldAddToCart = 'buy' in route.query
const product: Ref<Shopify.Product> = ref()

const populate = [
    'hero',
    'croppedHero',
    'wordmark',
    'packDetails.gameDetails.data',
    'packDetails.gameDetails.title',
    'packDetails.gameDetails.wordmark',
    'packDetails.gameDetails.croppedHero',
    'packDetails.gameDetails.hero',
    'packDetails.gameDetails.features',
    'packDetails.gameDetails.features.counts',
    'packDetails.gameDetails.features.options',
    'packDetails.gameDetails.features.types',
    'packDetails.gameDetails.packDetails',
    'promotional',
    'seo',
    'shopifyProduct',
    'tile'
]

const { data: bundle } = await useAsyncData<JBGWeb.BundleDetail>(
    `bundledetail-${bundleSlug}-${i18n.locale.value}`,
    async () => {
        try {
            const bundle = await app.$cms.fetchOne<JBGWeb.BundleDetail>('bundle-details', bundleSlug, {
                populate,
                locale: i18n.locale.value,
                countryCode: currentCountry.code
            })
            return bundle
        } catch (error) {
            if (error.message === 'No data') {
                // force a fetch in en
                try {
                    const bundle = await app.$cms.fetchOne<JBGWeb.BundleDetail>('bundle-details', bundleSlug, {
                        populate,
                        locale: 'en',
                        countryCode: currentCountry.code
                    })
                    fellBackToEn.value = true
                    return bundle
                } catch (e) {
                    console.error('error loading pack-details', error)
                    throw error
                }
            }
            console.error('error loading pack-details', error)
            throw error
        }
    }
)
product.value = bundle.value.shopifyProduct
let title = ''
if (bundle.value?.title) {
    title += `${bundle.value.title}`
}
if (i18n.locale.value !== 'en') {
    title += ` (${i18n.t(I18n.getLocaleNameTag(i18n.locale.value as I18n.Locale))})`
}

const shouldReload = computed(() => {
    let res = false
    // check what currency is present from the fetched data and compare to geolocation cookie
    if (product.value.priceRange.maxVariantPrice.currencyCode) {
        const cc = product.value.priceRange.maxVariantPrice.currencyCode
        const doesMatch = currencyCodeCloudfrontRegionMap[cc].includes(currentCountry.code)
        if (!doesMatch) res = true
    }
    return res
})

watch(shouldReload, async () => {
    if (shouldReload.value === true) {
        await refreshNuxtData(`bundledetail-${bundleSlug}-${i18n.locale.value}`)
        product.value = bundle.value.shopifyProduct
    }
}, { immediate: true })

useHead({
    title,
    meta: [
        {
            property: 'og:title',
            content: title
        },
        {
            property: 'og:image',
            content: bundle?.value.tile?.data?.attributes?.url
        },
        {
            name: 'description',
            content: bundle?.value.seo?.metaDescription ?? bundle?.value.shortDescription
        }
    ],
    link: [
        {
            rel: 'canonical',
            href: `https://www.jackboxgames.com/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'x-default',
            href: `https://www.jackboxgames.com/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'en',
            href: `https://www.jackboxgames.com/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'fr',
            href: `https://www.jackboxgames.com/fr/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'it',
            href: `https://www.jackboxgames.com/it/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'de',
            href: `https://www.jackboxgames.com/de/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'es-ES',
            href: `https://www.jackboxgames.com/es/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'es',
            href: `https://www.jackboxgames.com/es/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'es-MX',
            href: `https://www.jackboxgames.com/es-XL/games/bundles/${bundleSlug}`
        },
        {
            rel: 'alternate',
            hreflang: 'pt-BR',
            href: `https://www.jackboxgames.com/pt-BR/games/bundles/${bundleSlug}`
        }
    ]
})

const packsInBundle = computed((): JBGWeb.PackDetail[] | null => {
    if (!bundle.value) return null
    return bundle.value.packDetails?.data.map((gd) => gd.attributes)
})

const gameDetailsPerPack = computed(() => {
    if (!packsInBundle) return null

    // gameDetails is an object within each pack
    const gameDetails: Array<JBGWeb.GameDetail[]> = []
    packsInBundle.value.forEach((pack, idx) => {
        gameDetails[idx] = pack.gameDetails.data.map((gd) => gd.attributes)
    })
    return gameDetails
})

const gamesInBundle = computed(() => {
    if (!gameDetailsPerPack) return null

    const gamesInBundle: JBGWeb.GameDetail[] = []
    gameDetailsPerPack.value.forEach((pack) => {
        pack.forEach((game: JBGWeb.GameDetail) => {
            gamesInBundle.push(game)
        })
    })
    return gamesInBundle
})

const gameType = computed(() => (game: JBGWeb.GameDetail) => game.features?.types?.data[0]?.attributes)

const heroStyles = computed((): CSSProperties => {
    if (windowWidth.value > 599) {
        return {
            backgroundImage: `linear-gradient(180deg, rgba(26, 31, 38, 0.00) 95%, #161921 100%),
            radial-gradient(199.81% 112.67% at 92.6% 64.19%,
            rgba(26, 31, 38, 0.00) 15.98%, rgba(26, 31, 38, 0.69) 60.91%, #1A1F26 100%),
            url('${bundle.value?.hero?.data?.attributes.url}?format=auto')`
        }
    }

    return {
        backgroundImage: `var(--pack-details-linear-bg,
        radial-gradient(654% 98% at 50% 100%, #191d24,
        rgba(26, 31, 38, 0.77) 25%, rgba(26, 31, 38, 0) 100%)),
        url('${bundle.value?.croppedHero?.data?.attributes.url}?format=auto')`
    }
})

const specs = computed(() => {
    if (!gameDetailsPerPack.value) return null
    if (!gamesInBundle.value) return null

    const specs: Record<string, any> = {
        playerCounts: [] as number[],
        types: [] as string[],
        durations: [] as number[],
        ffSetting: [] as boolean[],
        modSetting: [] as boolean[],
        audienceSetting: [] as boolean[]
    }

    gamesInBundle.value.forEach(((game) => {
        const features = game.features

        if (features.counts) {
            specs.playerCounts.push(features.counts.playerMin)
            specs.playerCounts.push(features.counts.playerMax)
        }

        if (features.durationAvg !== 0) {
            specs.durations.push(features.durationAvg)
        }

        if (!features.types.data) {
            features.types.data.forEach((i) => {
                specs.types.push(i.attributes.name)
            })
        }

        if (features.options) {
            specs.ffSetting.push(features.options.hasFamilyFriendlyMode)
            specs.modSetting.push(features.options.hasModeration)
            specs.audienceSetting.push(features.options.hasAudience)
        }
    }))
    return specs
})

const onResize = (): void => {
    windowWidth.value = window.innerWidth
}

async function addToCart(bundle: JBGWeb.BundleDetail): Promise<void> {
    const shopifyProduct = bundle.shopifyProduct
    await app.$shopify.createLineItemAndAddToCart(shopifyProduct, shopifyProduct.variants[0], 1)
    ShoppingCart.open()
}

onMounted(() => {
    if (import.meta.client) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window.addEventListener('resize', onResize)
        onResize()
    }
    if (shouldAddToCart) {
        void addToCart(bundle.value)
    }
})

onBeforeUnmount(() => {
    if (import.meta.client) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window.removeEventListener('resize', onResize)
    }
})

const getPlayerRange = (game: JBGWeb.GameDetail) => {
    if (!game.features || !game.features.counts) return null
    return `${game.features.counts.playerMin} - ${game.features.counts.playerMax}`
}

const getDuration = (game: JBGWeb.GameDetail) => {
    if (!game.features || !game.features.durationAvg) return null
    return game.features.durationAvg
}

const carouselClick = (itemIndex: number) => {
    void app.$modal.showModal(
        CarouselModal as Component,
        {
            media: bundle.value?.promotional?.data,
            itemIndex
        }
    )
}

const linkUrl = (game: JBGWeb.GameDetail) => {
    if (!game.packDetails) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return localeRoute({
            name: 'games-gameslug',
            params: {
                gameslug: game.slug
            }
        })
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return localeRoute({
        name: 'games-packs-packslug-gameslug',
        params: {
            packslug: game.packDetails?.data?.attributes.slug,
            gameslug: game.slug
        }
    })
}

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.bundle-details{
    .hero {
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        position: relative;

        max-height: 810px;
        min-height: 600px;
        justify-content: space-between;

        @include mq-large-and-more {
            min-height: 960px !important;
        }

        @include mq-large {
            min-height: 700px;
            margin: -138px 0 0 0;
        }

        @include mq-medium {
            min-height: 650px;
            margin: -138px 0 0 0;
        }

        @include mq-small-and-less {
            min-height: 650px;
            margin: -138px 0 0 0;
        }

        @include mq-xsmall {
            padding: 113px 0 0 0;
            width: 100%;
            max-height: 982px;
        }

        .shopping-overlay {
            height: 100%;
            padding: 100px 0 0 38px;
            display: grid;
            align-items: center;

            @include mq-large-and-more {
                padding: 100px 0 0 0;
            }

            @include mq-xsmall {
                align-self: flex-end;
            }
        }
    }

    .about {
        .flex-row {
            .description{
                width: 50%;
                margin-right: 39px;

                @include mq-small-and-less {
                    width: 100%;
                }
            }

            .specs {
                width: 50%;
            }
        }

        .heading {
            display: block;
            font-size: 1.5em;
            margin-block-start: 0.83em;
            margin-block-end: 0.83em;
            font-weight: bold;
        }
    }

    .about-container {
        .description{
            margin-bottom: 40px;
        }

        .details {
            background: var(--surface-200);
            border-radius: 8px;
            padding: 16px;

            .detail:not(:last-child) {
                border-bottom: 1px solid var(--neutral-500);
            }

            .detail {
                align-items: center;
                display: inline-flex;
                justify-content: space-between;

                .fa-circle-check,
                .fa-circle-xmark {
                    color: var(--primary-200);
                }

                p {
                    padding-top: 12px;
                    max-width: 50%;
                }

                .right {
                    text-align: right;
                }
            }
        }
    }

    .game-details {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        margin: 16px 0px 16px 24px;
        overflow-x: scroll;
        padding: 20px 0px;

            //TODO: overflow hidden and make it draggable. use swiper?
            // temp scrollbar styling
            &::-webkit-scrollbar {
                border-radius: 100px;
                background: none;
                cursor: pointer;
                width: 14px;
                z-index: 1;
            }

            &::-webkit-scrollbar-thumb {
                background: var(--primary-200);
                border: 3px solid var(--surface-500);
                border-radius: 100px;
                position: relative;
            }

        .game-card {
            background: var(--surface-200);
            border-radius: 16px;
            box-shadow: 0px 9px 46px 8px rgba(0, 0, 0, 0.12),
            0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 11px 15px 0px rgba(0, 0, 0, 0.20);
            cursor: pointer;
            height: calc(100% - 10px);
            margin: 0px 20px;
            padding: 16px;
            width: 400px;

            @include mq-medium {
                width: 324px;
            }

            @include mq-small-and-less {
                width: 324px;
                transform: scale(.8);
                margin: -80px 0px;
                margin-left: -28px;
                height: auto;
                min-height: 640px;
            }

            .cropped-hero {
                border-radius: 12px 12px 0px 0px;
                margin: -16px;
                width: calc(100% + 32px);
                max-height: 392px;
            }

            .tag-container {
                display: inline-flex;
                flex-wrap: wrap;
                margin: 0px 0px 12px 0px;
            }

            .wordmark {
                filter: drop-shadow(0 0 0.75rem var(--surface-500));
                max-height: 120px;
                max-width: 70%;
                margin-top: -60px;
            }

            .description {
                color: #fff;
            }
        }
    }
}

</style>
